import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { getProducts, type Product } from '../../utils/supabase-data';

export default function ProduitPage() {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const products = await getProducts();
      const foundProduct = products.find(p => p.id === id);
      
      if (foundProduct) {
        setProduct(foundProduct);
        setSelectedColor(foundProduct.colors[0]);
        setSelectedSize(foundProduct.sizes[0]);
        
        // Charger les produits similaires
        const related = products.filter(p => p.id !== id).slice(0, 4);
        setRelatedProducts(related);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = () => {
    if (!product) return;

    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find(
      (item: any) => 
        item.id === product.id && 
        item.size === selectedSize && 
        item.color === selectedColor
    );

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        size: selectedSize,
        color: selectedColor,
        quantity
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="pt-24 pb-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <i className="ri-loader-4-line text-6xl text-gray-300 animate-spin mb-4"></i>
            <p className="text-gray-600">Chargement du produit...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="pt-24 pb-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Produit non trouvé</h1>
            <Link to="/boutique" className="text-black underline cursor-pointer">
              Retour à la boutique
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WhatsAppButton />

      {/* Notification */}
      {showNotification && (
        <div className="fixed top-20 right-3 left-3 sm:left-auto sm:right-4 bg-[#F2A7C3] text-white px-4 py-3 rounded-lg shadow-lg z-50 text-center sm:text-left text-sm">
          {t('produit.addedToCart')}
        </div>
      )}

      <div className="pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mb-5 md:mb-8 overflow-x-auto whitespace-nowrap">
            <a href="/" className="hover:text-[#F2A7C3] cursor-pointer shrink-0">
              {t('produit.home')}
            </a>
            <span>/</span>
            <a href="/boutique" className="hover:text-[#F2A7C3] cursor-pointer shrink-0">
              {t('produit.shop')}
            </a>
            <span>/</span>
            <span className="text-gray-900 truncate">{product.name}</span>
          </div>

          {/* Product Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 mb-12 md:mb-16">
            {/* Image Gallery */}
            <div className="space-y-3 md:space-y-4">
              {(() => {
                const allImages: string[] = [product.image];
                if (product.images && Array.isArray(product.images)) {
                  product.images.forEach((img: string) => {
                    if (img && img.trim() !== '') allImages.push(img);
                  });
                }
                const safeIndex = selectedImage < allImages.length ? selectedImage : 0;
                return (
                  <>
                    <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-gray-100">
                      <img
                        src={allImages[safeIndex]}
                        alt={product.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    {allImages.length > 1 && (
                      <div className="grid grid-cols-4 gap-2 md:gap-4">
                        {allImages.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImage(index)}
                            className={`relative aspect-[3/4] overflow-hidden rounded-lg bg-gray-100 transition-all cursor-pointer ${
                              selectedImage === index
                                ? 'ring-2 ring-pink-500 ring-offset-1'
                                : 'hover:ring-2 hover:ring-gray-300'
                            }`}
                          >
                            <img
                              src={image}
                              alt={`${product.name} - Photo ${index + 1}`}
                              className="w-full h-full object-cover object-top"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Product Info */}
            <div className="pt-0 md:pt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 leading-tight">{product.name}</h1>
              <p className="text-2xl md:text-3xl font-semibold text-[#F2A7C3] mb-5 md:mb-6">{product.price} TND</p>

              {/* Size Selection */}
              <div className="mb-5 md:mb-6">
                <h3 className="font-semibold mb-2 md:mb-3 text-sm">{t('produit.size')}</h3>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 md:px-6 md:py-3 border rounded-md transition-colors whitespace-nowrap cursor-pointer text-sm ${
                        selectedSize === size
                          ? 'bg-[#F2A7C3] text-white border-[#F2A7C3]'
                          : 'border-gray-300 hover:border-[#F2A7C3]'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Selection */}
              <div className="mb-5 md:mb-6">
                <h3 className="font-semibold mb-2 md:mb-3 text-sm">{t('produit.color')}</h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color.name)}
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 transition-all cursor-pointer ${
                        selectedColor === color.name
                          ? 'border-black scale-110'
                          : 'border-gray-300 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6 md:mb-8">
                <h3 className="font-semibold mb-2 md:mb-3 text-sm">{t('produit.quantity')}</h3>
                <div className="flex items-center gap-3 md:gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="text-xl font-semibold w-10 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={addToCart}
                className="w-full bg-[#F2A7C3] hover:bg-[#e891b0] text-white py-4 rounded-md mb-6 whitespace-nowrap cursor-pointer text-sm font-bold tracking-widest transition-colors"
              >
                {t('produit.addToCart')}
              </button>

              {/* Description */}
              <div className="border-t pt-5 md:pt-6">
                <h3 className="font-semibold mb-2 md:mb-3 text-sm">{t('produit.description')}</h3>
                <p className="text-gray-600 mb-3 md:mb-4 text-sm leading-relaxed">{product.description}</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2 text-sm">
                    <i className="ri-check-line text-[#F2A7C3] mt-0.5 shrink-0"></i>
                    <span>{t('produit.qualityBullet')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <i className="ri-check-line text-[#F2A7C3] mt-0.5 shrink-0"></i>
                    <span>{t('produit.fitBullet')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <i className="ri-check-line text-[#F2A7C3] mt-0.5 shrink-0"></i>
                    <span>{t('produit.designBullet')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Related Products */}
          <div className="border-t pt-8 md:pt-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">{t('produit.youMightLike')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {relatedProducts.map((relatedProduct) => (
                <a
                  key={relatedProduct.id}
                  href={`/produit/${relatedProduct.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded-lg mb-2 sm:mb-4 bg-gray-100">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="font-semibold text-xs sm:text-sm mb-1 group-hover:text-[#F2A7C3] transition-colors leading-tight">
                    {relatedProduct.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{relatedProduct.price} TND</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}